<script lang="ts" setup>
import type { TLDCatalogItem } from '@/types/dashboard/domain'
import { formatCurrency } from '@/utils'

defineProps<{
  items: TLDCatalogItem[]
  loading?: boolean
  disabled?: boolean
}>()

const emit = defineEmits<{
  add: [tld: string, category: string]
}>()

const getCategoryColor = (category: string | undefined): string => {
  switch (category) {
    case 'generic': return 'primary'
    case 'cctld': return 'success'
    case 'new_gtld': return 'warning'
    default: return 'default'
  }
}

const getCategoryLabel = (category: string | undefined): string => {
  switch (category) {
    case 'generic': return 'Generic'
    case 'cctld': return 'ccTLD'
    case 'new_gtld': return 'New gTLD'
    default: return 'Other'
  }
}
</script>

<template>
  <div class="tld-table">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div v-for="i in 5" :key="i" class="skeleton-row">
        <div class="skeleton-name"></div>
        <div class="skeleton-category"></div>
        <div class="skeleton-price"></div>
        <div class="skeleton-action"></div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="items.length === 0" class="empty-state">
      <v-icon icon="mdi-magnify" size="32" />
      <p>Keine TLDs gefunden</p>
    </div>

    <!-- TLD List -->
    <div v-else class="tld-list">
      <div
        v-for="item in items"
        :key="item.tld"
        class="tld-row"
      >
        <span class="tld-name">.{{ item.tld }}</span>
        <v-chip
          size="x-small"
          :color="getCategoryColor(item.category)"
          class="tld-category"
        >
          {{ getCategoryLabel(item.category) }}
        </v-chip>
        <span class="tld-price">
          <template v-if="item.register_price">
            {{ formatCurrency(item.register_price / 100, 'EUR', 'de-DE') }}/Jahr
          </template>
          <template v-else-if="item.price">
            {{ formatCurrency(item.price / 100, 'EUR', 'de-DE') }}/Jahr
          </template>
        </span>
        <v-chip
          v-if="item.promo"
          size="x-small"
          color="success"
          class="promo-badge"
        >
          Promo
        </v-chip>
        <v-btn
          icon="mdi-plus"
          variant="tonal"
          color="primary"
          size="small"
          :disabled="disabled"
          class="add-btn"
          @click="emit('add', item.tld, item.category || 'other')"
        />
      </div>
    </div>

    <!-- Item Count -->
    <div v-if="!loading && items.length > 0" class="item-count">
      {{ items.length }} TLDs verfugbar
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tld-table {
  max-height: 400px;
  overflow-y: auto;
}

/* Loading Skeleton */
.loading-state {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
}

.skeleton-name,
.skeleton-category,
.skeleton-price,
.skeleton-action {
  background: linear-gradient(
    90deg,
    rgba(120, 163, 255, 0.08) 25%,
    rgba(120, 163, 255, 0.15) 50%,
    rgba(120, 163, 255, 0.08) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-name {
  width: 60px;
  height: 20px;
}

.skeleton-category {
  width: 70px;
  height: 24px;
  border-radius: 12px;
}

.skeleton-price {
  width: 80px;
  height: 18px;
  margin-left: auto;
}

.skeleton-action {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  color: rgba(200, 218, 255, 0.4);

  .v-icon {
    margin-bottom: 12px;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

/* TLD List */
.tld-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tld-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(120, 163, 255, 0.15);
  }

  .tld-name {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    min-width: 50px;
  }

  .tld-category {
    flex-shrink: 0;
  }

  .tld-price {
    margin-left: auto;
    font-size: 0.85rem;
    color: rgba(200, 218, 255, 0.6);
  }

  .promo-badge {
    flex-shrink: 0;
  }

  .add-btn {
    flex-shrink: 0;
  }
}

/* Item Count */
.item-count {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.4);
  text-align: center;
}

/* Light Theme */
.v-theme--lightTheme {
  .tld-row {
    background: rgba(15, 23, 42, 0.02);
    border-color: rgba(15, 23, 42, 0.08);

    .tld-name {
      color: rgba(15, 23, 42, 0.9);
    }

    .tld-price {
      color: rgba(15, 23, 42, 0.6);
    }
  }
}

@media (max-width: 600px) {
  .tld-row {
    flex-wrap: wrap;

    .tld-price {
      width: 100%;
      margin-left: 0;
      margin-top: 4px;
    }
  }
}
</style>
